import React, { useState, useEffect, useCallback } from 'react';
import { assetUrl } from '../utils/assets';
import { useSwipeable } from 'react-swipeable';
import { motion } from 'motion/react';
import NextButton from './NextButton';
import ChapterSummaryPage from './ChapterSummaryPage';
import DemoCompletedPage from './DemoCompletedPage';
import AnimatedText from './AnimatedText';
import { StorySection as StorySectionType, PendingDiceRoll, ActionDifficulty, EffectStatus } from '../types';
import { useNarration } from '../context/NarrationContext';
import { useSoundEffects } from '../context/SoundEffectsContext';
import { useBackgroundMusic } from '../context/BackgroundMusicContext';

const DIFFICULTY_LABELS: Record<ActionDifficulty, string> = {
  trivial: 'Trivial',
  easy: 'Easy',
  challenging: 'Challenging',
  very_challenging: 'Very Challenging',
  impossible: 'Impossible',
};

const DIFFICULTY_COLORS: Record<ActionDifficulty, string> = {
  trivial: '#28a745',
  easy: '#5cb85c',
  challenging: '#7c4dff',
  very_challenging: '#dc3545',
  impossible: '#6c757d',
};

interface StoryPagerProps {
  storySections: StorySectionType[];
  showContinueButton: boolean;
  onContinue: () => void;
  continueButtonText?: string;
  isAwaitingResponse?: boolean;
  sessionId?: string;
  onOpenSurvey?: (origin: 'demo-end' | 'feedback-button', demoEndAnswers?: { wantToContinue: boolean; wantToGiveFeedback: boolean }) => void;
  skipContinueSound?: boolean;
  pendingDiceRoll?: PendingDiceRoll | null;
  activeEffects?: EffectStatus[];
}

const StoryPager: React.FC<StoryPagerProps> = ({
  storySections,
  showContinueButton,
  onContinue,
  continueButtonText,
  isAwaitingResponse = false,
  sessionId = '',
  onOpenSurvey,
  skipContinueSound = false,
  pendingDiceRoll,
  activeEffects = []
}) => {
  // Find the chapter window (from last chapter_start to end)
  const chapterStartIndex = storySections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.section_type === 'chapter_start')
    .slice(-1)[0]?.index ?? 0;

  // Get the sections within current chapter
  const chapterWindow = storySections.slice(chapterStartIndex);

  // Current page index within the chapter window
  // Always start at the last section (state machine controls flow)
  const [currentIndex, setCurrentIndex] = useState(chapterWindow.length - 1);

  const { playSection } = useNarration();
  const { playSound } = useSoundEffects();
  const { play: playMusic, playFromRandom: playMusicFromRandom, fadeOut: fadeOutMusic } = useBackgroundMusic();

  // When new sections are added, always jump to the newest
  // The state machine controls when sections appear, so this is always correct
  useEffect(() => {
    if (chapterWindow.length > 0) {
      setCurrentIndex(chapterWindow.length - 1);
    }
  }, [storySections.length]);

  useEffect(() => {
    const currentSection = chapterWindow[currentIndex];

    if (currentSection?.section_type === 'story_content' && sessionId) {
      playSection(sessionId, currentSection.section_id);
    }
  }, [currentIndex, sessionId, playSection]);

  // Background music for chapter covers
  useEffect(() => {
    const currentSection = chapterWindow[currentIndex];
    const isChapterStart = currentSection?.section_type === 'chapter_start';

    if (!isChapterStart) {
      fadeOutMusic();
      return;
    }

    playMusicFromRandom();
  }, [currentIndex, chapterWindow, playMusic, playMusicFromRandom, fadeOutMusic]);

  // Navigation handlers
  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      playSound('page_turn');
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex, playSound]);

  const goNext = useCallback(() => {
    if (currentIndex < chapterWindow.length - 1) {
      playSound('page_turn');
      setCurrentIndex(currentIndex + 1);
    } else if (showContinueButton) {
      playSound('page_turn');
      onContinue();
    }
  }, [currentIndex, chapterWindow.length, showContinueButton, onContinue, playSound]);

  // Check if input/textarea/contentEditable is focused
  const isInputFocused = useCallback(() => {
    const activeElement = document.activeElement;
    return activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      (activeElement as HTMLElement).contentEditable === 'true'
    );
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isInputFocused()) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goPrev, goNext, isInputFocused]);

  // Swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedRight: () => {
      if (!isInputFocused()) goNext();
    },
    onSwipedLeft: () => {
      if (!isInputFocused()) goPrev();
    },
    delta: 40,
    preventScrollOnSwipe: true,
    touchEventOptions: { passive: false }
  });

  // If no sections in current chapter, show nothing
  if (chapterWindow.length === 0) {
    return null;
  }

  // Get current section
  const currentSection = chapterWindow[currentIndex];

  // Skip sections with empty content unless it's a special section (chapter_start, chapter_end, chapter_failed, or demo_completed)
  const hasContent = currentSection?.html_content && currentSection.html_content.trim() !== '';
  const isChapterStart = currentSection?.section_type === 'chapter_start';
  const isChapterEnd = currentSection?.section_type === 'chapter_end';
  const isChapterFailed = currentSection?.section_type === 'chapter_failed';
  const isDemoCompleted = currentSection?.section_type === 'demo_completed';

  if (!hasContent && !isChapterStart && !isChapterEnd && !isChapterFailed && !isDemoCompleted) {
    return (
      <div className="story-pager">
        <div className="story-content">
          <p><em>The Time Masters would like to apologize for this abrupt disruption in the space-time continuum. Rest assured, this is exceedingly rare — the Masters very rarely apologize! Let us pick the story up from before the disruption.</em></p>
        </div>
        <div className="continue-button-area">
          <NextButton
            onClick={() => {
              onContinue();
            }}
            text="Resume Timeline"
          />
        </div>
      </div>
    );
  }

  // Determine if we're at the last page
  const isLastPage = currentIndex === chapterWindow.length - 1;

  // Render demo completed page
  if (isDemoCompleted && onOpenSurvey) {
    return (
      <div className="story-pager demo-completed-page" {...swipeHandlers}>
        <DemoCompletedPage
          sessionId={sessionId}
          onOpenSurvey={onOpenSurvey}
        />
      </div>
    );
  }

  // Render chapter failed page (time loop reset)
  if (isChapterFailed) {
    return (
      <div className="story-pager story-page-showcase" {...swipeHandlers}>
        <div className="pager-content layout-top">
          <div className="pager-text story-text-container">
            <div className="story-text text-center">
              <div className="text-4xl mb-4">⏳</div>
              <h2 className="text-xl font-bold text-amber-800 mb-4">Time Loop Reset</h2>
              <p className="text-slate-700 leading-relaxed">
                {currentSection.html_content ? (
                  <span dangerouslySetInnerHTML={{ __html: currentSection.html_content }} />
                ) : (
                  "The timeline shimmers and resets. Don't worry—your memories of what worked (and what didn't) remain intact."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom controls */}
        <div className="pager-controls bottom">
          <button
            className="pager-back-arrow"
            onClick={goPrev}
            disabled={currentIndex === 0}
            aria-label="Previous section"
          >
            ←
          </button>

          {/* Progress dots */}
          <div className="progress-dots">
            {chapterWindow.map((_, index) => (
              <span
                key={index}
                className={`progress-dot ${index === currentIndex ? 'active' : ''}`}
                aria-label={`Section ${index + 1} of ${chapterWindow.length}`}
              />
            ))}
          </div>

          {isLastPage && showContinueButton ? (
            <NextButton
              onClick={() => {
                if (!skipContinueSound) playSound('page_turn');
                onContinue();
              }}
              text="Try Again"
              loading={isAwaitingResponse}
            />
          ) : (
            <button
              className="pager-next-arrow"
              onClick={goNext}
              disabled={isLastPage && !showContinueButton}
              aria-label="Next section"
            >
              →
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render chapter summary page
  if (isChapterEnd && currentSection.stats) {
    return (
      <div className="story-pager chapter-summary-page" {...swipeHandlers}>
        <ChapterSummaryPage stats={currentSection.stats} />

        {/* Bottom controls with progress dots */}
        <div className="pager-controls bottom">
          <button
            className="pager-back-arrow"
            onClick={goPrev}
            disabled={currentIndex === 0}
            aria-label="Previous section"
          >
            ←
          </button>

          {/* Progress dots */}
          <div className="progress-dots">
            {chapterWindow.map((_, index) => (
              <span
                key={index}
                className={`progress-dot ${index === currentIndex ? 'active' : ''}`}
                aria-label={`Section ${index + 1} of ${chapterWindow.length}`}
              />
            ))}
          </div>

          {isLastPage && showContinueButton ? (
            <NextButton
              onClick={() => {
                if (!skipContinueSound) playSound('page_turn');
                onContinue();
              }}
              text={continueButtonText || "Continue to Next Chapter"}
              loading={isAwaitingResponse}
            />
          ) : (
            <button
              className="pager-next-arrow"
              onClick={goNext}
              disabled={isLastPage && !showContinueButton}
              aria-label="Next section"
            >
              →
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render chapter hero page
  if (isChapterStart) {
    return (
      <div className="story-pager story-page-hero" {...swipeHandlers}>
        <div className="chapter-hero-content">
          {currentSection.image && currentSection.image.url && (
            <div className="chapter-hero-image">
              <img
                src={assetUrl(currentSection.image.url)}
                alt={`Chapter ${currentSection.chapter_info?.number} cover`}
              />
            </div>
          )}
        </div>

        <div className="chapter-hero-overlay">
          <button
            className="chapter-start-button"
            onClick={onContinue}
            disabled={!showContinueButton}
          >
            Start Chapter
          </button>
        </div>
      </div>
    );
  }

  // Render normal story pages with layout priorities
  const layoutClass = `story-page-${currentSection.image_layout_priority}`;
  const imagePosition = currentSection.image?.position || 'left';

  return (
    <div
      key={currentSection.section_id}
      className={`story-pager ${layoutClass}`}
      {...swipeHandlers}
    >
      <div className={`pager-content layout-${imagePosition}`}>
        <div className="pager-inner">
          {/* Section image */}
          {currentSection.image && currentSection.image.url && (
            <div className="pager-image">
              <motion.img
                src={assetUrl(currentSection.image.url)}
                alt={`Story illustration for section ${currentSection.section_id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              />
            </div>
          )}

          {/* Section content */}
          <div className="pager-text story-text-container">
            <div className="story-text">
              <AnimatedText htmlContent={currentSection.html_content} speed={50} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom controls with progress dots */}
      <div className="pager-controls bottom">
        <button
          className="pager-back-arrow"
          onClick={goPrev}
          disabled={currentIndex === 0}
          aria-label="Previous section"
        >
          ←
        </button>

        {/* Progress dots */}
        <div className="progress-dots">
          {chapterWindow.map((_, index) => (
            <span
              key={index}
              className={`progress-dot ${index === currentIndex ? 'active' : ''}`}
              aria-label={`Section ${index + 1} of ${chapterWindow.length}`}
            />
          ))}
        </div>

        {isLastPage && showContinueButton ? (
          <div className="continue-button-area">
            {/* Show difficulty info before "Let's Roll" button */}
            {pendingDiceRoll && continueButtonText?.includes("Roll") && (
              <div className="dice-preview-info">
                <span
                  className="difficulty-badge-small"
                  style={{ backgroundColor: DIFFICULTY_COLORS[pendingDiceRoll.diceRollResult.difficulty] }}
                >
                  {DIFFICULTY_LABELS[pendingDiceRoll.diceRollResult.difficulty]}
                </span>
                {pendingDiceRoll.diceRollResult.difficulty !== 'trivial' &&
                 pendingDiceRoll.diceRollResult.difficulty !== 'impossible' &&
                 pendingDiceRoll.diceRollResult.target_needed > 0 && (
                  <span className="target-preview">
                    Need {pendingDiceRoll.diceRollResult.target_needed}+
                  </span>
                )}
              </div>
            )}
            {/* Show active effects from paradox cards */}
            {pendingDiceRoll && activeEffects.length > 0 && (
              <div className="active-effects-preview">
                {activeEffects.map((effect, i) => (
                  <span key={i} className="effect-pill-small">
                    {effect.description} <span className="effect-source">({effect.source_card})</span>
                  </span>
                ))}
              </div>
            )}
            {/* Show condition feedback for conditional modifiers */}
            {pendingDiceRoll && pendingDiceRoll.diceRollResult.modifiers_applied.some(m => m.condition_met !== null && m.condition_met !== undefined) && (
              <div className="condition-feedback-section">
                {pendingDiceRoll.diceRollResult.modifiers_applied
                  .filter(m => m.condition_met !== null && m.condition_met !== undefined)
                  .map((mod, i) => (
                    <div key={i} className={`condition-feedback-item ${mod.condition_met ? 'met' : 'not-met'}`}>
                      <span className="condition-icon">{mod.condition_met ? '✓' : '✗'}</span>
                      <span className="condition-text">
                        {mod.condition_met
                          ? `${mod.description}: +${mod.value}`
                          : `Not ${mod.description.toLowerCase()}: +${mod.value} (not applied)`}
                      </span>
                    </div>
                  ))}
              </div>
            )}
            <NextButton
              onClick={() => {
                if (!skipContinueSound) playSound('page_turn');
                onContinue();
              }}
              text={continueButtonText || (currentSection.section_type === 'chapter_end' ? 'View Chapter Summary' : 'Continue')}
              loading={isAwaitingResponse}
            />
          </div>
        ) : (
          <button
            className="pager-next-arrow"
            onClick={goNext}
            disabled={isLastPage && !showContinueButton}
            aria-label="Next section"
          >
            →
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(StoryPager);

